import { forwardRef, useCallback, useEffect, useId, useImperativeHandle, useMemo, useRef, useState } from 'react'
import { MilkdownProvider } from '@milkdown/react'
import { useTranslation } from 'react-i18next'

import { cn } from '@/components/new/lib/utils'
import { Field, FieldError } from '@/components/new/ui/field'

import { getRichTextEditorCopy } from './copy'
import { RichTextEditorCore, RichTextEditorCoreHandle } from './RichTextEditorCore'
import { RichTextEditorInlineFormattingMenu } from './RichTextEditorInlineFormattingMenu'
import { RichTextEditorToolbar } from './RichTextEditorToolbar'
import { getRichTextEditorActionDefinitions, RichTextEditorActionId } from './editor/actions'
import { TextTagPicker } from './TextTagPicker'
import { RichTextEditorError, RichTextEditorHandle, RichTextEditorProps } from './types'

import './RichTextEditor.css'

const emptyTags: NonNullable<RichTextEditorProps['tags']> = []

export const RichTextEditor = forwardRef<RichTextEditorHandle, RichTextEditorProps>((props, ref) => {
    const {
        allowTagInsert = true,
        className,
        compact = false,
        editorClassName,
        errors = [],
        features: { codeBlock = false, headings = true, quote = false, taskList = false } = {},
        id,
        imageUpload,
        mode = 'rich',
        onErrorsChange,
        tags = emptyTags,
        renderToolbarEnd,
        syntaxHighlighting,
    } = props

    const { t } = useTranslation()
    const copy = useMemo(() => getRichTextEditorCopy(t as (key: string, options?: Record<string, unknown>) => string), [t])
    const coreRef = useRef<RichTextEditorCoreHandle>(null)
    const validationInputRef = useRef<HTMLInputElement>(null)
    const generatedId = useId()
    const [internalErrors, setInternalErrors] = useState<RichTextEditorError[]>([])
    const [showLengthErrors, setShowLengthErrors] = useState(false)
    const [hasTextSelection, setHasTextSelection] = useState(false)
    const actionDefinitions = useMemo(
        () =>
            mode === 'rich' || mode === 'rich-inline'
                ? getRichTextEditorActionDefinitions({
                      codeBlock,
                      copy,
                      headings,
                      imageUploadEnabled: !!imageUpload,
                      mode,
                      quote,
                      taskList,
                  })
                : [],
        [codeBlock, copy, headings, imageUpload, mode, quote, taskList]
    )
    const useInlineInputShell = mode === 'rich-inline' || (mode === 'inline' && actionDefinitions.length === 0)

    const publicApi = useMemo<RichTextEditorHandle>(
        () => ({
            focus: () => coreRef.current?.focus(),
            insertText: (text) => coreRef.current?.insertText(text),
            insertTag: (tag) => coreRef.current?.insertTag(tag),
        }),
        []
    )

    useImperativeHandle(ref, () => publicApi, [publicApi])

    useEffect(() => {
        onErrorsChange?.(internalErrors)
    }, [internalErrors, onErrorsChange])

    const handleValidationErrorsChange = useCallback((nextErrors: RichTextEditorError[]) => {
        setInternalErrors((currentErrors) => {
            const unchanged =
                currentErrors.length === nextErrors.length &&
                currentErrors.every(
                    (currentError, index) =>
                        currentError.type === nextErrors[index]?.type && currentError.message === nextErrors[index]?.message
                )

            return unchanged ? currentErrors : nextErrors
        })
    }, [])

    const lengthErrors = useMemo(
        () => internalErrors.filter(({ type }) => type === 'minLength' || type === 'maxLength'),
        [internalErrors]
    )
    const visibleInternalErrors = useMemo(
        () =>
            showLengthErrors
                ? internalErrors
                : internalErrors.filter(({ type }) => type !== 'minLength' && type !== 'maxLength'),
        [internalErrors, showLengthErrors]
    )
    const allErrors = useMemo(
        () => [
            ...errors.map(
                (message) =>
                    ({
                        type: 'external',
                        message,
                    }) satisfies RichTextEditorError
            ),
            ...visibleInternalErrors,
        ],
        [errors, visibleInternalErrors]
    )
    const lengthErrorMessage = useMemo(() => lengthErrors.map(({ message }) => message).join('\n'), [lengthErrors])

    useEffect(() => {
        validationInputRef.current?.setCustomValidity(lengthErrorMessage)
    }, [lengthErrorMessage])

    const hasInlineFormattingMenu = mode === 'rich-inline' && actionDefinitions.length > 0
    const hasInlineTagPicker = allowTagInsert && tags.length > 0
    const inlineEndContent =
        useInlineInputShell && (hasInlineFormattingMenu || hasInlineTagPicker || renderToolbarEnd) ? (
            <>
                {hasInlineFormattingMenu ? (
                    <RichTextEditorInlineFormattingMenu
                        actions={actionDefinitions}
                        compact={compact}
                        disabled={props.readOnly}
                        hasTextSelection={hasTextSelection}
                        label={copy.groups.inline}
                        onRunAction={(actionId) => coreRef.current?.runAction(actionId)}
                    />
                ) : null}
                {renderToolbarEnd ? renderToolbarEnd(publicApi) : null}
                {hasInlineTagPicker ? (
                    <TextTagPicker
                        disabled={props.readOnly}
                        copy={copy}
                        tags={tags}
                        onSelect={publicApi.insertTag}
                        compactButton={compact}
                        panelStyle={mode === 'rich-inline'}
                    />
                ) : null}
            </>
        ) : null

    return (
        <Field data-invalid={allErrors.length > 0} className={cn('relative gap-0', className)}>
            <input
                ref={validationInputRef}
                id={id ?? generatedId}
                className="pointer-events-none absolute left-0 top-0 size-px opacity-0"
                tabIndex={-1}
                aria-invalid={allErrors.length > 0 || undefined}
                disabled={props.readOnly}
                maxLength={props.maxLength}
                minLength={props.minLength}
                required={props.required}
                value={props.value}
                onChange={() => undefined}
                onFocus={publicApi.focus}
                onInvalid={(event) => {
                    event.preventDefault()
                    setShowLengthErrors(true)
                    publicApi.focus()
                }}
            />
            <div
                className={cn(
                    !useInlineInputShell &&
                        [
                            'rich-text-editor border-input bg-background shadow-xs focus-within:border-ring',
                            'focus-within:ring-ring/50 dark:bg-input/30 focus-within:ring-3 rounded-md border',
                            'transition-[color,box-shadow]',
                        ].join(' '),
                    !useInlineInputShell && 'overflow-hidden',
                    !useInlineInputShell &&
                        allErrors.length > 0 &&
                        'border-destructive ring-destructive/20 dark:border-destructive/50 dark:ring-destructive/40 ring-3',
                    editorClassName
                )}
                data-mode={mode}
                data-syntax-highlighting={syntaxHighlighting ? 'true' : undefined}
            >
                {!useInlineInputShell ? (
                    <RichTextEditorToolbar
                        api={publicApi}
                        allowTagInsert={allowTagInsert}
                        compact={compact}
                        codeBlock={codeBlock}
                        imageUploadEnabled={!!imageUpload}
                        copy={copy}
                        headings={headings}
                        mode={mode}
                        quote={quote}
                        tags={tags}
                        taskList={taskList}
                        hasTextSelection={hasTextSelection}
                        readOnly={props.readOnly}
                        renderToolbarEnd={renderToolbarEnd}
                        onRunAction={(actionId: RichTextEditorActionId) => coreRef.current?.runAction(actionId)}
                    />
                ) : null}
                <MilkdownProvider>
                    <RichTextEditorCore
                        ref={coreRef}
                        {...props}
                        copy={copy}
                        imageUpload={imageUpload}
                        invalid={allErrors.length > 0}
                        inlineEndContent={inlineEndContent}
                        mode={mode}
                        tags={tags}
                        onValidationErrorsChange={handleValidationErrorsChange}
                        onSelectionStateChange={setHasTextSelection}
                        useInlineInputShell={useInlineInputShell}
                    />
                </MilkdownProvider>
            </div>
            <FieldError errors={allErrors.map((error) => ({ message: error.message }))} />
        </Field>
    )
})

RichTextEditor.displayName = 'RichTextEditor'
